import React, { useEffect, useState } from 'react';
import { Box, Card, CardMedia, CardContent, Typography, Avatar, CircularProgress } from '@mui/material';
import { Language as LinkIcon, CheckCircle, PlayArrow } from '@mui/icons-material';

const getYouTubeVideoId = (url) => {
  try {
    const match = url.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
    );
    return match?.[1] || null;
  } catch {
    return null;
  }
};

const getVimeoVideoId = (url) => {
  try {
    const match = url.match(/vimeo\.com\/(\d+)/);
    return match?.[1] || null;
  } catch {
    return null;
  }
};

const VideoEmbed = ({ url, onPlayClick }) => {
  const youtubeId = getYouTubeVideoId(url);
  const vimeoId = getVimeoVideoId(url);
  const [active, setActive] = useState(false);

  const embedSrc = youtubeId
    ? `https://www.youtube.com/embed/${youtubeId}?rel=0&autoplay=1`
    : `https://player.vimeo.com/video/${vimeoId}?autoplay=1`;

  const thumbnailSrc = youtubeId
    ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`
    : null;

  return (
    <Box
      sx={{
        mt: 2,
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'black',
      }}
    >
      <Box sx={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}>
        {active ? (
          <Box
            component="iframe"
            title="Embedded video"
            src={embedSrc}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
            sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms allow-presentation"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 0,
            }}
          />
        ) : (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              if (onPlayClick) {
                onPlayClick();
              } else {
                setActive(true);
              }
            }}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover .play-icon': { transform: 'scale(1.1)' },
            }}
          >
            {thumbnailSrc && (
              <Box
                component="img"
                src={thumbnailSrc}
                alt="Video thumbnail"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            )}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(0,0,0,0.25)',
              }}
            />
            <Box
              className="play-icon"
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'transform 0.15s ease',
                position: 'relative',
                zIndex: 1,
                boxShadow: '0 2px 8px rgba(0,0,0,0.6)',
              }}
            >
              <PlayArrow sx={{ fontSize: 44, color: '#e00' }} />
            </Box>
          </Box>
        )}
      </Box>
      {/* Source label */}
      <Box sx={{ px: 1.5, py: 0.75, bgcolor: 'background.paper', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          component="img"
          src={youtubeId ? 'https://www.youtube.com/favicon.ico' : 'https://vimeo.com/favicon.ico'}
          alt=""
          sx={{ width: 14, height: 14, borderRadius: '2px' }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, fontWeight: 500 }}>
          {youtubeId ? 'YouTube' : 'Vimeo'} · {url}
        </Typography>
      </Box>
    </Box>
  );
};

const LinkPreview = ({ linkData, url: directUrl, variant = 'default', onPlayClick }) => {
  const [metadata, setMetadata] = useState(linkData || null);
  const [loading, setLoading] = useState(!linkData && !!directUrl);
  const [error, setError] = useState(false);

  const url = directUrl || linkData?.url;

  // Detect video URLs before hooks — used to skip metadata fetch
  const youtubeId = url ? getYouTubeVideoId(url) : null;
  const vimeoId = url ? getVimeoVideoId(url) : null;
  const isVideoUrl = !!(youtubeId || vimeoId);

  // All hooks must be called unconditionally before any early returns
  useEffect(() => {
    if (isVideoUrl) return;
    if (!linkData && directUrl) {
      fetchMetadata(directUrl);
    }
  }, [directUrl, linkData, isVideoUrl]);

  // If it's a video URL, render the embed directly — no need for metadata
  if (isVideoUrl) {
    return <VideoEmbed url={url} onPlayClick={onPlayClick} />;
  }

  const fetchMetadata = async (urlToFetch) => {
    try {
      setLoading(true);
      setError(false);

      // For other URLs, extract domain
      const urlObj = new URL(urlToFetch);
      setMetadata({
        url: urlToFetch,
        title: urlObj.hostname,
        description: urlToFetch,
        image: null,
        siteName: urlObj.hostname.toUpperCase(),
      });
      setLoading(false);
    } catch (err) {
      console.error('Error fetching URL metadata:', err);
      setError(true);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card sx={{ mt: 2, p: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Card>
    );
  }

  if (error || !metadata || !metadata.url) return null;

  const { url: metaUrl, title, description, image, siteName, favicon } = metadata;

  const handleClick = (e) => {
    e.stopPropagation();
    window.open(metaUrl, '_blank', 'noopener,noreferrer');
  };

  // Compact variant for comments and messages
  if (variant === 'compact') {
    return (
      <Card
        onClick={handleClick}
        sx={{
          display: 'flex',
          cursor: 'pointer',
          maxWidth: 400,
          mt: 1,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        {image && (
          <CardMedia
            component="img"
            sx={{ width: 100, height: 100, objectFit: 'cover' }}
            image={image}
            alt={title}
          />
        )}
        <CardContent sx={{ flex: 1, p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            {favicon ? (
              <Avatar src={favicon} sx={{ width: 16, height: 16, mr: 0.5 }} />
            ) : (
              <LinkIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
            )}
            <Typography variant="caption" color="text.secondary" noWrap>
              {siteName || new URL(metaUrl).hostname}
            </Typography>
          </Box>
          {title && (
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }} noWrap>
              {title}
            </Typography>
          )}
          {description && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {description}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  }

  // Default variant for posts (aligned with mobile design)
  return (
    <Card
      onClick={handleClick}
      sx={{
        cursor: 'pointer',
        mt: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        '&:hover': {
          boxShadow: 3,
        },
      }}
    >
      {/* URL Header with verification checkmark (mobile-style) */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        p: 1.5, 
        backgroundColor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Typography 
          variant="body2" 
          sx={{ 
            flex: 1, 
            color: 'primary.main', 
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {metaUrl}
        </Typography>
        <CheckCircle sx={{ fontSize: 20, color: '#4CAF50', ml: 1 }} />
      </Box>

      {image && (
        <CardMedia
          component="img"
          sx={{ height: 200, objectFit: 'cover', backgroundColor: '#e0e0e0' }}
          image={image}
          alt={title}
        />
      )}
      <CardContent sx={{ backgroundColor: 'background.paper' }}>
        <Typography 
          variant="caption" 
          color="text.secondary"
          sx={{ 
            fontWeight: 600, 
            textTransform: 'uppercase',
            display: 'block',
            mb: 0.5
          }}
        >
          {siteName || new URL(metaUrl).hostname}
        </Typography>
        {title && (
          <Typography 
            variant="h6" 
            fontWeight={700} 
            sx={{ fontSize: 16, lineHeight: 1.25, mb: 0.5 }}
          >
            {title}
          </Typography>
        )}
        {description && description !== metaUrl && (
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: 13, lineHeight: 1.4 }}
          >
            {description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default LinkPreview;
